import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  fetchUserAgenda,
  fetchUserAgendaCalendars,
  createUserAgendaEvent,
  updateUserAgendaEvent,
  deleteUserAgendaEvent,
  createUserAgendaCalendar,
  updateUserAgendaCalendar,
  deleteUserAgendaCalendar,
} from "../api/client";
import { toAgendaQueryBounds } from "../pages/TeamAgenda/agendaUtils";

function unwrapAgenda(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

export function useUserAgenda({ from, to, enabled = true } = {}) {
  const { fromStr, toStr } = toAgendaQueryBounds(from, to);

  return useQuery(
    ["user-agenda", fromStr, toStr],
    async () => {
      const res = await fetchUserAgenda({ from: fromStr, to: toStr });
      return unwrapAgenda(res) || { events: [] };
    },
    { enabled, staleTime: 30_000, keepPreviousData: true }
  );
}

export function useUserAgendaCalendars({ enabled = true } = {}) {
  return useQuery(
    ["user-agenda-calendars"],
    async () => {
      const res = await fetchUserAgendaCalendars();
      return unwrapAgenda(res) || { calendars: [] };
    },
    { enabled, staleTime: 60_000 }
  );
}

export function useUserAgendaMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries(["user-agenda"]);

  const createMutation = useMutation(
    async (payload) => {
      const res = await createUserAgendaEvent(payload);
      return unwrapAgenda(res);
    },
    { onSuccess: invalidate }
  );

  const updateMutation = useMutation(
    async ({ eventId, payload }) => {
      const res = await updateUserAgendaEvent(eventId, payload);
      return unwrapAgenda(res);
    },
    { onSuccess: invalidate }
  );

  const deleteMutation = useMutation(
    async (eventId) => deleteUserAgendaEvent(eventId),
    { onSuccess: invalidate }
  );

  const createCalendarMutation = useMutation(
    async (payload) => {
      const res = await createUserAgendaCalendar(payload);
      return unwrapAgenda(res);
    },
    {
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries(["user-agenda-calendars"]);
      },
    }
  );

  const updateCalendarMutation = useMutation(
    async ({ calendarId, payload }) => {
      const res = await updateUserAgendaCalendar(calendarId, payload);
      return unwrapAgenda(res);
    },
    {
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries(["user-agenda-calendars"]);
      },
    }
  );

  const deleteCalendarMutation = useMutation(
    async (calendarId) => deleteUserAgendaCalendar(calendarId),
    {
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries(["user-agenda-calendars"]);
      },
    }
  );

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    createCalendarMutation,
    updateCalendarMutation,
    deleteCalendarMutation,
    invalidate,
  };
}
